import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { careComparison } from "@/lib/content";
import { useLocation } from "@/contexts/LocationContext";

/**
 * WarmCompare — "Compare your options" decision aid (AFH vs assisted living vs
 * nursing home vs in-home care). Families choose a *category* before a home; this
 * educates honestly during that comparison and positions the adult family home —
 * including being upfront about when it's NOT the right fit.
 *
 * Responsive: a 4-up card grid (stacks on mobile). Our category is highlighted with
 * an accent ring + badge. Brand tokens only; cost is qualitative (no invented figures).
 */
const ROWS: { key: keyof (typeof careComparison)[number]; label: string }[] = [
  { key: "setting", label: "Setting" },
  { key: "size", label: "Size" },
  { key: "attention", label: "Attention" },
  { key: "care", label: "Care level" },
  { key: "cost", label: "Typical cost" },
  { key: "bestFor", label: "Best for" },
];

export function WarmCompare() {
  const { isSeattle } = useLocation();
  const basePath = isSeattle ? "/seattle" : "";

  return (
    <section className="py-20">
      <div className="container mx-auto px-4">
        <div className="text-center">
          <span className="eyebrow text-accent-strong">Making Sense of the Choices</span>
          <h2 className="mt-4 display-md font-heading font-semibold tracking-[-0.025em] text-foreground">
            Compare your{" "}
            <span className="font-display-italic font-normal text-accent-strong">options</span>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">
            Most families are weighing a few kinds of care at once. Here's an honest
            look at how they differ — so you can find the right fit, even if it isn't us.
          </p>
        </div>

        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {careComparison.map((option) => (
            <div
              key={option.name}
              className={`relative flex flex-col rounded-3xl border bg-card p-6 shadow-[var(--shadow-card)] ${
                option.highlight ? "border-accent/50 ring-2 ring-accent/30" : "border-border/50"
              }`}
            >
              {option.highlight && (
                <span className="absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full bg-accent px-3 py-1 text-xs font-semibold text-accent-foreground shadow-sm">
                  <Check className="h-3 w-3" />
                  Our home
                </span>
              )}
              <h3 className="font-heading text-lg font-semibold text-foreground">{option.name}</h3>
              <dl className="mt-4 space-y-3">
                {ROWS.map((row) => (
                  <div key={row.label}>
                    <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      {row.label}
                    </dt>
                    <dd className="mt-0.5 text-sm text-foreground">{option[row.key] as string}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>

        {/* Honest disqualifier — telling families when an AFH is wrong builds trust. */}
        <div className="mx-auto mt-10 max-w-2xl rounded-2xl border border-border/50 bg-muted/40 p-6 text-center">
          <p className="text-sm leading-relaxed text-muted-foreground">
            An adult family home isn't right for everyone. If your loved one needs
            intensive, round-the-clock skilled nursing, a different setting may serve them
            better — and we're glad to point you toward it.
          </p>
          <Link
            to={`${basePath}/contact#tour-form`}
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary underline-offset-4 transition-colors hover:text-accent-strong hover:underline"
          >
            Not sure which fits? Talk it through with us
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
